/**
 * The Leviticus 11 clean/unclean list.
 *
 * One authored list, served from PocketBase (`food_classifications`, see Torahly's
 * `backend/food_classifications_setup.md`). The apps read the same record. Nothing here decides
 * what is clean; it only reads what Torahly published.
 *
 * Read is public and needs no token, because knowing whether a food is clean is observance and
 * observance is never gated.
 *
 * Unlike the apps there is no bundled floor. A browser with no network and no cache has nothing
 * to fall back on, so the check reports itself unverifiable rather than guessing, which is the
 * same answer the apps give when they have never seen a list. Guessing here would mean telling a
 * woman her recipe is fine because we could not check it.
 */

export type Tier = 'clean' | 'caution' | 'unclean' | 'unknown'

export interface SourceQualifier {
  keywords: string[]
  tier: Tier
  note: string
}

export interface IngredientRule {
  id: string
  names: string[]
  tier: Tier
  reason: string
  whatItIs: string
  references: string[]
  sourceQualifiers?: SourceQualifier[]
}

export interface Classifications {
  version: number
  publishedAt?: string
  ingredients: IngredientRule[]
}

const BASE = 'https://api.torahly.app'
const CACHE_KEY = 'covella.classifications'

function readCache(): Classifications | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Classifications
    return Array.isArray(parsed.ingredients) ? parsed : null
  } catch {
    return null
  }
}

function writeCache(value: Classifications): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(value))
  } catch {
    /* a full localStorage costs a refetch, nothing more */
  }
}

let inMemory: Classifications | null = null

/**
 * The newest list this browser can see: the server when reachable, the cache otherwise.
 *
 * Compared on `version` rather than on which answered, so a cached copy is never replaced by an
 * older publish. Null means neither source produced anything, and callers must treat that as
 * "cannot verify", never as "nothing is wrong".
 */
export async function loadClassifications(): Promise<Classifications | null> {
  if (inMemory) return inMemory

  const cached = readCache()

  try {
    const response = await fetch(
      `${BASE}/api/collections/food_classifications/records?sort=-version&perPage=1`,
    )
    if (response.ok) {
      const body = (await response.json()) as { items?: { data?: Classifications }[] }
      const fromServer = body.items?.[0]?.data
      if (
        fromServer &&
        Array.isArray(fromServer.ingredients) &&
        fromServer.ingredients.length > 0 &&
        (!cached || fromServer.version > cached.version)
      ) {
        writeCache(fromServer)
        inMemory = fromServer
        return fromServer
      }
    }
  } catch {
    /* offline, or the server is down. The cache is the answer if there is one. */
  }

  inMemory = cached
  return cached
}

/*
 * ---------------------------------------------------------------------------------------------
 * Everything below is a line-by-line port of `food/RecipeCleanCheck.kt`.
 *
 * The two implementations answer the same question about the same list for the same woman, so
 * they have to agree, and a disagreement here shows up as a wrong verdict rather than as an
 * error. `golden/clean_check_cases.json` pins both. Change one side and the fixture fails on the
 * other, which is the point of it. Do not "improve" anything here without changing the Kotlin
 * and the fixture in the same breath.
 * ---------------------------------------------------------------------------------------------
 */

/**
 * Word-boundary matching, not substring. This is the whole correctness question.
 *
 * A naive `includes` flags "hamburger" and "grahams" for `ham`, and "crabapple" for `crab`.
 * Exact equality is not the fix either, because it misses "clams" for `clam`.
 *
 * The boundaries are `(?<![a-z])` and `(?![a-z])` rather than `\b`, matching the Kotlin exactly.
 * They are not the same thing: `\b` counts digits as word characters, so "ham2" would fail a
 * `\b` boundary and pass this one.
 */
export function mentions(haystack: string, needle: string): boolean {
  const trimmed = needle.trim().toLowerCase()
  if (!trimmed) return false
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  // (?:e?s)? absorbs "clam"/"clams", "prawn"/"prawns", "tomato"/"tomatoes".
  return new RegExp(`(?<![a-z])${escaped}(?:e?s)?(?![a-z])`).test(haystack)
}

/** Lowercase, punctuation to spaces, runs of whitespace collapsed, trimmed. As the Kotlin. */
export function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

export const PROTEIN_TAGS = {
  BEEF: 'beef',
  LAMB: 'lamb',
  GOAT: 'goat',
  POULTRY: 'poultry',
  CLEAN_FISH: 'clean_fish',
  EGGS: 'eggs',
  PLANT_WHOLE: 'plant_whole',
} as const

const ANIMAL_TAGS: string[] = [
  PROTEIN_TAGS.BEEF,
  PROTEIN_TAGS.LAMB,
  PROTEIN_TAGS.GOAT,
  PROTEIN_TAGS.POULTRY,
  PROTEIN_TAGS.CLEAN_FISH,
  PROTEIN_TAGS.EGGS,
]

/** Torahly groups lamb and goat in one rule, so a match there yields both tags. */
function tagsForRuleId(ruleId: string): string[] {
  switch (ruleId) {
    case 'beef':
    case 'bison_venison':
      return [PROTEIN_TAGS.BEEF]
    case 'lamb_goat':
      return [PROTEIN_TAGS.LAMB, PROTEIN_TAGS.GOAT]
    case 'chicken_turkey':
    case 'duck_goose':
      return [PROTEIN_TAGS.POULTRY]
    case 'finned_scaled_fish':
      return [PROTEIN_TAGS.CLEAN_FISH]
    case 'eggs':
      return [PROTEIN_TAGS.EGGS]
    default:
      return []
  }
}

export interface Finding {
  /** Her own ingredient line, untouched, so she can see which one is being questioned. */
  ingredientName: string
  ruleId: string
  tier: Tier
  reason: string
  references: string[]
  qualifierNote: string
}

export type Verdict = 'CLEAN' | 'NEEDS_SOURCE' | 'REJECTED' | 'UNVERIFIABLE'

export interface CheckResult {
  verdict: Verdict
  findings: Finding[]
  derivedProteinTags: string[]
}

/**
 * The three verdicts, plus the one that means we could not look.
 *
 * REJECTED is the only one that stops a submission. NEEDS_SOURCE does not: unspecified gelatin
 * genuinely is not saying pig or cow, and refusing her recipe would claim knowledge nobody has.
 * UNVERIFIABLE means no list was reachable at all, and must never be reported as clean.
 *
 * Note that a CLEAN match produces a protein tag, not a finding. Findings are only ever the
 * things being questioned, which is what lets the submit screen list them without listing every
 * ordinary ingredient back at her.
 */
export function check(ingredientNames: string[], rules: IngredientRule[]): CheckResult {
  if (rules.length === 0) {
    return { verdict: 'UNVERIFIABLE', findings: [], derivedProteinTags: [] }
  }

  const findings: Finding[] = []
  const proteinTags: string[] = []
  const addTag = (tag: string) => {
    if (!proteinTags.includes(tag)) proteinTags.push(tag)
  }

  /*
   * A rule with no usable `names` is skipped rather than trusted or thrown on.
   *
   * The list is published data this browser did not write, and it is cached, so one malformed
   * rule used to take the whole page down and keep taking it down until the cache was cleared by
   * hand. Skipping is the only honest option of the three: matching on a rule with no names is
   * impossible, and crashing turns a data problem into a product that will not open. If skipping
   * empties the list, the guard below reports UNVERIFIABLE, which is the answer the product
   * already promises for a list it cannot read.
   */
  const usable = rules.filter((rule) => Array.isArray(rule?.names) && rule.names.length > 0)
  if (usable.length === 0) {
    return { verdict: 'UNVERIFIABLE', findings: [], derivedProteinTags: [] }
  }

  for (const rawName of ingredientNames) {
    const name = normalize(rawName)
    if (!name) continue

    for (const rule of usable) {
      if (!rule.names.some((candidate) => mentions(name, candidate))) continue

      // A source qualifier resolves an otherwise ambiguous rule: "bovine gelatin" and
      // "agar agar" both land here and settle the question the bare word left open.
      const qualifier = rule.sourceQualifiers?.find((q) =>
        q.keywords.some((keyword) => mentions(name, keyword)),
      )
      const effectiveTier: Tier = qualifier?.tier ?? rule.tier

      if (effectiveTier === 'clean') {
        for (const tag of tagsForRuleId(rule.id)) addTag(tag)
      } else if (effectiveTier === 'caution' || effectiveTier === 'unclean') {
        findings.push({
          ingredientName: rawName.trim(),
          ruleId: rule.id,
          tier: effectiveTier,
          reason: qualifier?.note?.trim() ? qualifier.note : rule.reason,
          references: rule.references,
          qualifierNote: qualifier?.note ?? '',
        })
      }
      // 'unknown' is ignored, as in the Kotlin.
    }
  }

  // No animal protein matched at all: the recipe stands on plants. Only claimed when the check
  // actually ran, which is why it sits after the empty-rules guard above.
  if (!proteinTags.some((tag) => ANIMAL_TAGS.includes(tag))) {
    addTag(PROTEIN_TAGS.PLANT_WHOLE)
  }

  const verdict: Verdict = findings.some((f) => f.tier === 'unclean')
    ? 'REJECTED'
    : findings.some((f) => f.tier === 'caution')
      ? 'NEEDS_SOURCE'
      : 'CLEAN'

  // distinctBy(ingredientName, ruleId): one line can trip the same rule through two of its
  // names, and she should be told once.
  const seen = new Set<string>()
  const distinct = findings.filter((f) => {
    const key = `${f.ingredientName}\u0000${f.ruleId}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  return { verdict, findings: distinct, derivedProteinTags: proteinTags }
}
